import React, {useCallback} from 'react';
import {Box} from '../../box';
import {GridList, GridListProps} from '../grid-list';
import {NFTGhostItem} from '../../nft-ghost-item';
import {NFTBackgroundItem} from '../../nft-background-item';
import {Typography} from '../../typography';
import {useBreakpoint} from '../../use-breakpoint';
import {getNumberOfItems} from './utils';

type SomeGridListProps<Item extends any> = Omit<
  GridListProps<Item>,
  'renderLoadingComponent' | 'renderErrorComponent' | 'numOfItems'
>;

export interface NftGridListProps<Item extends any>
  extends SomeGridListProps<Item> {
  numberOfLoadingItems?: number;
}

export const NftGridList = <Item extends any>({
  numberOfLoadingItems,
  renderItemContainer,
  ...rest
}: NftGridListProps<Item>): ReturnType<React.FC<NftGridListProps<Item>>> => {
  const {currentBreakpoint} = useBreakpoint();

  const renderLoadingComponent = useCallback(
    () =>
      new Array(numberOfLoadingItems || getNumberOfItems(currentBreakpoint))
        .fill(null)
        .map((nullItem, index) => (
          <Box minWidth={0} key={index.toString()}>
            {/* @ts-expect-error render container fix */}
            {renderItemContainer ? (
              renderItemContainer(
                <NFTGhostItem />,
                index.toString(),
                nullItem,
                index,
              )
            ) : (
              <NFTGhostItem key={index.toString()} />
            )}
          </Box>
        )),

    [currentBreakpoint, numberOfLoadingItems, renderItemContainer],
  );

  const renderBackgroundComponent = useCallback(
    () =>
      new Array(getNumberOfItems(currentBreakpoint))
        .fill(null)
        .map((nullItem, index) => (
          <Box minWidth={0} key={index.toString()}>
            {/* @ts-expect-error render container fix */}
            {renderItemContainer ? (
              renderItemContainer(
                <NFTBackgroundItem index={index} />,
                index.toString(),
                nullItem,
                index,
              )
            ) : (
              <NFTBackgroundItem key={index.toString()} index={index} />
            )}
          </Box>
        )),

    [currentBreakpoint, renderItemContainer],
  );

  const renderErrorComponent = useCallback(
    (error: {message: string}) => (
      <Box>
        <Typography textStyle="m" color="textDefault">
          {error.message}
        </Typography>
      </Box>
    ),
    [],
  );

  return (
    <GridList
      {...(rest as any)}
      numOfItems={
        rest.items.length ||
        numberOfLoadingItems ||
        getNumberOfItems(currentBreakpoint)
      }
      renderItemContainer={renderItemContainer}
      renderLoadingComponent={renderLoadingComponent}
      renderErrorComponent={renderErrorComponent}
      renderBackgroundComponent={renderBackgroundComponent}
    />
  );
};
